import LocationMarker from "@/components/mapping/LocationMarker"
import MeasureDistance from "@/components/mapping/MeasureDistance"
import type { LatLng, Nullable } from "@/models"
import { calcDistance } from "@/utils/distance"
import { watchImmediate } from "@vueuse/core"
import L, { Polyline, Popup, type Map } from "leaflet"
import { computed, ref, type ComputedRef, type FunctionalComponent } from "vue"

export interface MeasureDistanceDisplayData {
	isMeasuring: boolean
	startCoords: Nullable<LatLng>
	endCoords: Nullable<LatLng>
	distanceText: string
}

export function useMapDistanceMeasuring() {
	const isMeasuring = ref<boolean>(false)
	const distanceStartCoords = ref<Nullable<LatLng>>(null)
	const distanceEndCoords = ref<Nullable<LatLng>>(null)
	const distanceText = ref<string>("")

	let distanceStart: Nullable<LocationMarker>
	let distanceEnd: Nullable<LocationMarker>
	let distanceLine: Nullable<Polyline>
	let distancePopup: Nullable<Popup>

	let map: Map

	function initMeasuring(lMap: Map) {
		map = lMap

		if (!map) {
			return
		}

		map.addEventListener("click", (clickEvent) => {
			if (isMeasuring.value) {
				if (distanceStartCoords.value && distanceEndCoords.value) {
					distanceStartCoords.value = null
					distanceEndCoords.value = null
					distanceLine?.remove()
					distanceStart?.setRadius(0)
					distanceStart?.setDirection(0, 0)
					distanceText.value = ""
				}

				if (!distanceStartCoords.value) {
					distanceStartCoords.value = {
						latitude: clickEvent.latlng.lat,
						longitude: clickEvent.latlng.lng,
					}
				} else {
					distancePopup = null
					distanceEndCoords.value = {
						latitude: clickEvent.latlng.lat,
						longitude: clickEvent.latlng.lng,
					}
				}

				return
			}
		})

		map.addEventListener("mousemove", (mouseEvent) => {
			if (!distanceStartCoords.value || distanceEndCoords.value || !isMeasuring.value) {
				return
			}

			if (distanceLine) {
				distanceLine.remove()
			}

			const latLngTo: LatLng = { latitude: mouseEvent.latlng.lat, longitude: mouseEvent.latlng.lng }
			const calculatedDistance = calcDistance(distanceStartCoords.value, latLngTo)

			distanceText.value = calculatedDistance.distanceText
			distanceStart?.setRadius(calculatedDistance.distance)
			distanceStart?.setDirection(calculatedDistance.bearing, calculatedDistance.angle)

			if (!distancePopup) {
				distancePopup = L.popup({
					closeButton: false,
					closeOnEscapeKey: false,
					offset: [150, 40],
					className: "direction-popup",
				})
					.setLatLng(calculatedDistance.to)
					.setContent(distanceText.value)
					.openOn(map)
			}
			distancePopup.setLatLng(calculatedDistance.to)
			distancePopup.setContent(distanceText.value)

			distanceLine = L.polyline(calculatedDistance.coords, { color: "#1d8d5d" })
			distanceLine.addTo(map)
		})

		function startDistance() {
			if (!map) {
				return
			}

			const loc = distanceStartCoords.value
			if (!loc) {
				if (distanceStart) {
					distanceStart.removeFrom(map)
					distanceStart = null
				}
				return
			}

			if (distanceStart) {
				distanceStart.moveTo(loc.latitude, loc.longitude)
			} else {
				distanceStart = new LocationMarker({
					latitude: loc.latitude,
					longitude: loc.longitude,
					radius: 0,
					radiusOptions: {
						color: "#25ac72",
					},
					markerStyles: {
						filter: "hue-rotate(300deg) brightness(125%)",
					},
				})
				distanceStart.addTo(map)
			}
		}

		function endDistance() {
			if (!map) {
				return
			}

			const loc = distanceEndCoords.value
			if (!loc) {
				if (distanceEnd) {
					distanceEnd.removeFrom(map)
					distanceEnd = null
				}
				return
			}

			if (distanceEnd) {
				distanceEnd.moveTo(loc.latitude, loc.longitude)
			} else {
				distanceEnd = new LocationMarker({
					latitude: loc.latitude,
					longitude: loc.longitude,
					markerStyles: {
						filter: "hue-rotate(70deg)",
					},
				})
				distanceEnd.addTo(map)
			}
		}

		watchImmediate(distanceStartCoords, startDistance)
		watchImmediate(distanceEndCoords, endDistance)
	}

	const resetMeasure = () => {
		isMeasuring.value = !isMeasuring.value
		distanceStartCoords.value = null
		distanceEndCoords.value = null
		distanceLine?.remove()
		distanceText.value = ""
	}

	const measureData: ComputedRef<MeasureDistanceDisplayData> = computed(() => {
		return {
			isMeasuring: isMeasuring.value,
			startCoords: distanceStartCoords.value,
			endCoords: distanceEndCoords.value,
			distanceText: distanceText.value,
		}
	})

	const MeasureDistanceControl: FunctionalComponent = () => {
		return <MeasureDistance {...measureData.value} resetMeasure={resetMeasure} />
	}

	return {
		initMeasuring,
		measureData,
		resetMeasure,
		MeasureDistance: MeasureDistanceControl,
	}
}
